import { markReturnToCompanies } from '../features/contacts/companies/companyRoutes';
import { SIProvider, useSI } from '../contexts/SIContext';
import { SIInspector } from '../features/si/SIInspector';
import { useWorkspaceNavigation } from '../hooks/useWorkspaceNavigation';
import { buildViewPath } from '../utils/subRoutes';
import { useInitialProjectAccess } from '../hooks/useInitialProjectAccess';
import { useMainScreenWarm } from '../hooks/useMainScreenWarm';
import { useData } from '../contexts/DataContext';
import { useProjectPermissions } from '../features/projects/hooks/useProjectPermissions';
import { resolveProjectCalendarScope } from '../utils/projectCalendarRoute';
import { Skeleton } from '../shared/ui/skeleton';
import { Activity, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { ClientDashboard } from '../features/client/ClientDashboardNew';
import { CallProvider } from '../features/calls/CallContext';
import { HuddleProvider } from '../features/calls/HuddleContext';
import { NativePresenceBridge } from '../platform/presence/NativePresenceBridge';
import { IncomingCallOverlay } from '../features/calls/IncomingCallOverlay';
import { ActiveCallBar } from '../features/calls/ActiveCallBar';
import { VoicemailOverlay } from '../features/calls/VoicemailOverlay';
import { MicErrorBanner } from '../features/calls/CallStatusUI';
import {
  AppRightPanelProvider,
  BOQ_PANEL_MOTION_DURATION_MS,
} from '../contexts/AppRightPanelContext';
import { User, Info, ShieldCheck, Building2 } from '@/shared/ui/icons';
import { AdaptiveDeviceTemplate, TemplateSection } from '../layout-system';
import { AppLayout } from '../platform/navigation/AppLayout';
import { UnifiedAppHeader } from '../platform/navigation/UnifiedAppHeader';
import { AppSidebar } from '../platform/navigation/AppSidebar';
import type { GoogleDriveFile } from '../types/documents';
import type { Workspace } from '../types/workspace';
import type { AppView } from './navigation';
import {
  isAppView,
  HS_EDITOR_SECTION_PREFIX,
  HS_DOCUMENT_SECTION_PREFIX,
  INTERIM_SECTION_PREFIX,
  INTERIM_CLOSE_APPLICATION_SENTINEL,
  EMPLOYER_DETAIL_TAB_PREFIX,
  EMPLOYERS_CLOSE_DETAIL_SENTINEL,
} from './navigation';
import {
  EMPLOYERS_CLEAR_SELECTION_EVENT,
  EMPLOYER_DETAIL_SET_SECTION_EVENT,
} from '../features/employers/constants';
import { NotificationService } from '../services/NotificationService';
import { waitForTaskNotificationDisplay } from '../features/calendar/taskNotificationNavigation';
import { queueEmailNotificationOpen } from '../features/email/emailNotificationNavigation';
import { getBoqComment } from '../features/boq/services/BOQCommentsService';
import { queueBOQCommentTarget, waitForBOQCommentTarget } from '../features/boq/utils/boqCommentNavigation';
import { queueCalendarEventTarget, normalizeCalendarEventTarget, calendarEventTargetFromUrl } from '../features/calendar/calendarEventNavigation';
import { DataService } from '../services/DataService';
import { getGoogleMapsApiKey } from '../services/GoogleMapsService';
import { ProjectStagesService } from '../services/ProjectStagesService';
import { buildPrettyContactPath, slugifySegment } from '../utils/prettyRoutes';
import type { ContactPanelEntry } from '../features/contacts/ContactProfilePanel';
import { buildWorkspacePathById } from '../utils/prettyRouteBuilders';
import { canManageActiveWorkspaceSettings } from '../utils/permissions';
import { canOpenEmail, canOpenFinanceHubs, canOpenOutreach, canOpenWorkspaceDirectory, navigationGranted } from '../platform/navigation/mainScreenAccess';
import {
  mainNavigationDestination,
  getSidebarNavGroups,
  getSelfServiceWorkspaceUserSlug,
  getSelfServiceWorkspaceBotSlug,
  isSelfServiceWorkspaceTab,
  WORKSPACE_PROFILE_TAB_PREFIX,
} from '../platform/navigation/sidebarNavConfig';
import { useNativeBridge } from '../hooks/useNativeBridge';
import { NativeBridge } from '../services/NativeBridge';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { usePreferencesShortcut } from '../features/preferences/usePreferencesShortcut';
import { SettingsModal } from '../features/preferences/SettingsModal';
import { ShortcutsSheet } from '../features/preferences/components/ShortcutsSheet';
import { useSearchHotkeys } from '../platform/search/searchHotkeys';
import { QUANTISAI_TOGGLE_EVENT, useGlobalShortcuts } from '../platform/shortcuts/useGlobalShortcuts';
import { SISplitRestorer } from '../features/si/SISplitRestorer';
import { lazyChunk } from '../utils/lazyChunk';
import {
  OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT,
  type OpenWorkspaceResourceLibraryDetail,
} from '../features/resource-library/types';
import { isOutreachSectionId } from '../features/outreach/constants';
import { useCalendarTaskCreateIntent } from '../features/calendar/useCalendarTaskCreateIntent';
import { RequestAccess } from '../platform/RequestAccess';
import { buildAccessRequestTarget } from '../utils/accessRequestTarget';

const MembershipView = lazyChunk(() => import('../features/billing/MembershipView'), (m) => m.MembershipView);
const MessagesView = lazyChunk(() => import('../features/messages/MessagesView'), (m) => m.MessagesView);
const UserProfile = lazyChunk(() => import('../features/profile/UserProfile'), (m) => m.UserProfile);
const NotificationsView = lazyChunk(() => import('../features/notifications/NotificationsView'), (m) => m.NotificationsView);
const MessagesSidePanel = lazyChunk(() => import('../features/messages/MessagesSidePanel'), (m) => m.MessagesSidePanel);
const NotificationsSidePanel = lazyChunk(() => import('../features/notifications/NotificationsSidePanel'), (m) => m.NotificationsSidePanel);
const SupportModal = lazyChunk(() => import('../features/support/SupportModal'), (m) => m.SupportModal);
const CalendarTasksSidePanel = lazyChunk(() => import('../features/calendar/CalendarTasksSidePanel'), (m) => m.CalendarTasksSidePanel);
const BookingSidePanel = lazyChunk(() => import('../features/calendar/BookingSidePanel'), (m) => m.BookingSidePanel);
const AddProjectInspector = lazyChunk(
  () => import('../features/projects/add-project-form/AddProjectInspector'),
  (m) => m.AddProjectInspector,
);
const AddProjectSheet = lazyChunk(() => import('../features/projects/add-project-form/AddProjectSheet'), (m) => m.AddProjectSheet);
const ProjectDetail = lazyChunk(() => import('../features/projects/ProjectDetail'), (m) => m.ProjectDetail);
const QSDashboard = lazyChunk(() => import('../features/dashboard/QSDashboard'), (m) => m.QSDashboard);
const ProjectListView = lazyChunk(() => import('../features/projects/ProjectListView'), (m) => m.ProjectListView);
const WorkspaceList = lazyChunk(() => import('../features/workspaces/WorkspaceList'), (m) => m.WorkspaceList);
const WorkspaceDetail = lazyChunk(() => import('../features/workspaces/WorkspaceDetail'), (m) => m.WorkspaceDetail);
const WorkspaceBotsSection = lazyChunk(() => import('../features/workspaces/bots/WorkspaceBotsSection'), (m) => m.WorkspaceBotsSection);
const WorkspaceRequestForm = lazyChunk(() => import('../features/workspaces/WorkspaceRequestForm'), (m) => m.WorkspaceRequestForm);
const SystemSettings = lazyChunk(() => import('../features/settings/SystemSettings'), (m) => m.SystemSettings);
const PreferencesView = lazyChunk(() => import('../features/preferences/PreferencesView'), (m) => m.PreferencesView);
const UserDashboard = lazyChunk(() => import('../features/supervisor/UserDashboard'), (m) => m.UserDashboard);
const InterimValuationsView = lazyChunk(() => import('../features/interim/InterimValuationsView'), (m) => m.InterimValuationsView);
const SalesView = lazyChunk(() => import('../features/sales/SalesView'), (m) => m.SalesView);
const CISReturnsView = lazyChunk(() => import('../features/cis/CISReturnsView'), (m) => m.CISReturnsView);
const PurchasesView = lazyChunk(() => import('../features/purchases/PurchasesView'), (m) => m.PurchasesView);
const PayrollView = lazyChunk(() => import('../features/payroll/PayrollView'), (m) => m.PayrollView);
const AccountingView = lazyChunk(() => import('../features/accounting/AccountingView'), (m) => m.AccountingView);
const TaxView = lazyChunk(() => import('../features/tax/TaxView'), (m) => m.TaxView);
const FinanceBilling = lazyChunk(() => import('../features/billing/FinanceBilling'), (m) => m.FinanceBilling);
const EmployerManagement = lazyChunk(() => import('../features/employers'), (m) => m.EmployerManagement);
const AdminSupervisorManagement = lazyChunk(() => import('../features/supervisor/AdminSupervisorManagement'), (m) => m.AdminSupervisorManagement);
const UserManagement = lazyChunk(() => import('../features/users/UserManagement'), (m) => m.UserManagement);
const AddUserForm = lazyChunk(() => import('../features/users/AddUserForm'), (m) => m.AddUserForm);
const ResourceLibraryManagement = lazyChunk(() => import('../features/resource-library'), (m) => m.ResourceLibraryManagement);
const PlantRegisterView = lazyChunk(() => import('../features/plant'), (m) => m.PlantRegisterView);
const OutreachView = lazyChunk(() => import('../features/outreach'), (m) => m.OutreachView);
const CompaniesView = lazyChunk(() => import('../features/contacts/companies/CompaniesView'), (m) => m.CompaniesView);
const ContactProfilePanel = lazyChunk(() => import('../features/contacts/ContactProfilePanel'), (m) => m.ContactProfilePanel);
const ConsultancyPipelineView = lazyChunk(() => import('../features/pipeline/ConsultancyPipelineView'), (m) => m.ConsultancyPipelineView);
const WorkspacePipelineView = lazyChunk(() => import('../features/pipeline/WorkspacePipelineView'), (m) => m.WorkspacePipelineView);
const MobileSearchPage = lazyChunk(() => import('../platform/search/MobileSearchPage'), (m) => m.MobileSearchPage);
const CalendarPage = lazyChunk(() => import('../features/calendar/CalendarPage'), (m) => m.CalendarPage);
const EmailView = lazyChunk(() => import('../features/email/EmailView'));
const SharedInboxView = lazyChunk(() => import('../features/email/SharedInboxView'), (m) => m.SharedInboxView);
const MoreView = lazyChunk(() => import('../platform/navigation/MoreView'), (m) => m.MoreView);

interface AuthenticatedWorkspaceProps {
  auth: any;
  currentView: AppView;
  isNavigating?: boolean;
  shellConfig: {
    preset: any;
    scrollMode: any;
    noPadding: boolean;
  };
  shouldPassWebHeader: boolean;
  nativeShellHidesWebMainHeader?: boolean;
  pageTitle: string;
  navigateToView: (view: AppView) => void;
  handleLogout: () => void;
  toggleNotificationsPanel: () => void;
  toggleMessagesPanel: (chatId?: string, messageId?: string | null) => void;
  toggleTaskQueuePanel: () => void;
  toggleBookingPanel: () => void;
  notificationOpen: boolean;
  messagePanelOpen: boolean;
  taskQueueOpen: boolean;
  bookingPanelOpen: boolean;
  panelChatId: string | null;
  panelThreadMessageId: string | null;
  closeRightPanels: () => void;
  showContextualSearch: boolean;
  activateContextualSearch: () => void;
  projectActiveTab: string;
  setProjectActiveTab: (tab: string) => void;
  projectTabProps: any;
  projectPermissions: any;
  setProjectPermissions: (permissions: any) => void;
  projectListProps: any;
  workspacesActiveTab: any;
  setWorkspacesActiveTab: (tab: any) => void;
  workspacesStats: any;
  selectedProjectId: string | null;
  selectedProjectName: string | null;
  handleSelectProject: (projectId: string, projectName: string) => void;
  handleSelectProjectTab: (projectId: string, projectName: string, tab: string) => void;
  projectFilterStage: any;
  documentRouteSlug: string | null;
  setDocumentRouteSlug: (slug: string | null) => void;
  eventsRouteSlug: string | null;
  setEventsRouteSlug: (slug: string | null) => void;
  handleCloseProjectDetail: () => void;
  selectedTaskId: string | null;
  setSelectedTaskId: (id: string | null) => void;
  setTaskRouteSlug: (slug: string | null) => void;
  initialTaskId?: string;
  calendarProjectScope: string | null;
  handleShowCalendarWithTask: (taskId?: string) => void;
  handleShowInterimValuations: () => void;
  handleShowMessages: () => void;
  handleShowNotifications: () => void;
  handleShowDashboard: () => void;
  handleShowUserManagement: () => void;
  setCurrentView: (view: AppView) => void;
  handleShowAdminPanel: () => void;
  mainContractorInitialTab: any;
  /** The Employers screen switches between its directory and the add form on
   *  its own (header +, native +, Cancel), and the app shell preset and the
   *  URL both hang off this value — so the screen has to push its transitions
   *  back up here or the form renders under the collection preset at
   *  /employers and a /employers/add deeplink can never be escaped. */
  setMainContractorInitialTab: (value: 'contractors' | 'add-contractor') => void;
  selectedUserId: string | null;
  setSelectedUserId: (id: string | null) => void;
  newlyCreatedUser: any | null;
  setNewlyCreatedUser: (user: any | null) => void;
  userManagementActiveTab: string;
  setUserManagementActiveTab: (tab: string) => void;
  handleShowAddUserForm: () => void;
  selectedClientId: string | null;
  selectedClientName: string | null;
  handleSelectClient: (clientId: string, clientName: string, section?: string) => void;
  handleCloseClientDetail: () => void;
  clientDetailActiveTab: string;
  setClientDetailActiveTab: (tab: string) => void;
  systemSettingsActiveTab: string;
  setSystemSettingsActiveTab: (tab: string) => void;
  preferencesSection: string | null;
  setPreferencesSection: (section: string | null) => void;
  profileActiveTab: string;
  outreachActiveTab: string | null;
  setProfileActiveTab: (tab: string) => void;
  emailActiveTab: string;
  setEmailActiveTab: (tab: string) => void;
  handleShowSystemSettings: () => void;
  handleShowFinanceBilling: () => void;
  clientRequestActiveTab: string;
  setClientRequestActiveTab: (tab: string) => void;
  clientRequestCompletedTabs: Set<string>;
  setClientRequestCompletedTabs: (tabs: Set<string>) => void;
  addProjectActiveTab: any;
  setAddProjectActiveTab: (tab: any) => void;
  addProjectCompletedTabs: Set<any>;
  setAddProjectCompletedTabs: (tabs: Set<any>) => void;
  handleShowProjects: (stageFilter?: string) => void;
  handleShowMainContractors: (initialTab?: 'contractors' | 'add-contractor') => void; // prop name kept for compat
  interimInitialTab: string | undefined;
  pendingChatId: string | null;
  setPendingChatId: (id: string | null) => void;
  pendingChatSlug: string | null;
  setPendingChatSlug: (slug: string | null) => void;
  getCompany: (id?: string | null) => any;
  onNotesCountChange?: (count: number) => void;
}

function ProjectCalendarWithAccess({ projectId, ...props }: ComponentProps<typeof CalendarPage> & { projectId: string }) {
  const { permissions, loading } = useProjectPermissions(projectId, props.user.id);
  if (loading && !permissions) {
    return <div className="h-full p-4" aria-label="Loading project calendar"><Skeleton className="h-full w-full" /></div>;
  }
  if (!permissions) {
    return <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground" role="status">Project calendar unavailable. This project may no longer be accessible.</div>;
  }
  return <CalendarPage {...props} projectId={projectId} projectAccess={{
    tasks: permissions.canViewTasks,
    items: permissions.canViewItems,
    financial: permissions.canViewFinancial,
    applications: permissions.canViewApplications === true,
    milestones: permissions.canViewTasks && permissions.canViewFinancial,
  }} />;
}

function ScopedCalendarPage({ scope, ...props }: ComponentProps<typeof CalendarPage> & { scope: string | null }) {
  const { projects, companies, initialLoadState } = useData();
  if (scope === null) return <CalendarPage {...props} />;
  const project = resolveProjectCalendarScope(scope, projects, companies);
  if (!project && initialLoadState === 'loading') {
    return <div className="h-full p-4" aria-label="Loading project calendar"><Skeleton className="h-full w-full" /></div>;
  }
  if (!project) {
    return <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground" role="status">Project calendar unavailable. This project may no longer be accessible.</div>;
  }
  return <ProjectCalendarWithAccess {...props} key={project.id} projectId={project.id} />;
}


const SI_SPLIT_TABS = new Set(['pricing', 'planner']);
const SI_SPLIT_DEFAULT_WIDTH = 'calc((100vw - var(--qs-sidebar-actual-width, 0px)) * 0.4)';
const SI_SPLIT_RECORD_MIN_WIDTH = 480;


export function AuthenticatedWorkspace(props: AuthenticatedWorkspaceProps) {
  return <SIProvider key={`${props.auth.user.id}:${props.auth.activeWorkspaceId ?? ""}`}><AuthenticatedWorkspaceContent {...props} /></SIProvider>;
}

function AuthenticatedWorkspaceContent(props: AuthenticatedWorkspaceProps) {
  const si = useSI();
  const {
    auth,
    currentView,
    isNavigating = false,
    shellConfig,
    shouldPassWebHeader,
    nativeShellHidesWebMainHeader = false,
    pageTitle,
    navigateToView,
    handleLogout,
    toggleNotificationsPanel,
    toggleMessagesPanel,
    toggleTaskQueuePanel,
    toggleBookingPanel,
    notificationOpen,
    messagePanelOpen,
    taskQueueOpen,
    bookingPanelOpen,
    panelChatId,
    panelThreadMessageId,
    closeRightPanels,
    projectActiveTab,
    setProjectActiveTab,
    projectTabProps,
    projectPermissions,
    setProjectPermissions,
    projectListProps,
    workspacesActiveTab,
    setWorkspacesActiveTab,
    workspacesStats,
    selectedProjectId,
    selectedProjectName,
    handleSelectProject,
    handleSelectProjectTab,
    projectFilterStage,
    documentRouteSlug,
    setDocumentRouteSlug,
    eventsRouteSlug,
    setEventsRouteSlug,
    handleCloseProjectDetail,
    selectedTaskId,
    setSelectedTaskId,
    setTaskRouteSlug,
    initialTaskId,
    calendarProjectScope,
    handleShowCalendarWithTask,
    handleShowInterimValuations,
    handleShowMessages,
    handleShowNotifications,
    handleShowDashboard,
    handleShowUserManagement,
    setCurrentView,
    handleShowAdminPanel,
    mainContractorInitialTab,
    setMainContractorInitialTab,
    selectedUserId,
    setSelectedUserId,
    newlyCreatedUser,
    setNewlyCreatedUser,
    userManagementActiveTab,
    setUserManagementActiveTab,
    handleShowAddUserForm,
    selectedClientId,
    selectedClientName,
    handleSelectClient,
    handleCloseClientDetail,
    clientDetailActiveTab,
    setClientDetailActiveTab,
    systemSettingsActiveTab,
    setSystemSettingsActiveTab,
    preferencesSection,
    setPreferencesSection,
    profileActiveTab,
    outreachActiveTab,
    setProfileActiveTab,
    emailActiveTab,
    setEmailActiveTab,
    handleShowSystemSettings,
    handleShowFinanceBilling,
    clientRequestActiveTab,
    setClientRequestActiveTab,
    clientRequestCompletedTabs,
    setClientRequestCompletedTabs,
    addProjectActiveTab,
    setAddProjectActiveTab,
    addProjectCompletedTabs,
    setAddProjectCompletedTabs,
    handleShowProjects,
    handleShowMainContractors,
    interimInitialTab,
    pendingChatId,
    setPendingChatId,
    pendingChatSlug,
    setPendingChatSlug,
    getCompany,
    onNotesCountChange,
  } = props;
  // On mobile, New Project opens as a bottom sheet over the projects list
  // (desktop keeps the roomy full-screen two-pane view). Mobile breakpoint
  // is <1024px per the project convention.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  usePreferencesShortcut();
  useSearchHotkeys();
  useGlobalShortcuts({ canAddProject: auth.isAdmin() });
  const isCompactDesktop = useMediaQuery('(max-width: 1439px)');
  const isLaptopDesktop = useMediaQuery('(max-width: 1799px)');
  const isNarrowDesktop = useMediaQuery('(max-width: 2199px)');
  const isPricingOverviewRoute = currentView === 'project-detail' && projectActiveTab === 'pricing';
  // QuantisAI opened beside a bill, the programme or a document splits the body with it instead of floating.
  const siSplitTab = isDesktop && currentView === 'project-detail' && SI_SPLIT_TABS.has(projectActiveTab);
  const siSplit = si.open && siSplitTab;
  const pricingOverviewWidth = isCompactDesktop ? 240 : isLaptopDesktop ? 260 : 560;
  const boqInspectorDefaultWidth = isCompactDesktop
    ? 400
    : isLaptopDesktop
      ? 440
      : isNarrowDesktop
        ? 480
        : 520;
  const boqInspectorMaxWidth = isCompactDesktop
    ? 440
    : isLaptopDesktop
      ? 480
      : isNarrowDesktop
        ? 640
        : 800;
  const [addProjectSheetOpen, setAddProjectSheetOpen] = useState(false);
  const [addProjectInspectorOpen, setAddProjectInspectorOpen] = useState(false);
  const [resourceLibraryWorkspaceScope, setResourceLibraryWorkspaceScope] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const workspaceNavigation = useWorkspaceNavigation();
  useInitialProjectAccess(auth.user?.id, auth.activeWorkspaceId);
  useMainScreenWarm(auth, workspaceNavigation);
  const navigationDestination = mainNavigationDestination(currentView);
  const navigationAllowed = !navigationDestination || navigationGranted(workspaceNavigation, navigationDestination);
  const customerEnquiriesWorkspaceId = auth.activeWorkspaceId || auth.user?.workspaceId || auth.user?.workspace_id || null;
  const projectsGranted = workspaceNavigation.authoritative
    ? workspaceNavigation.navigation?.main.some(item => item.id === 'projects') === true : true;
  const customerEnquiriesGranted = workspaceNavigation.authoritative
    ? workspaceNavigation.navigation?.main.some(item => item.id === 'workspace-pipeline') === true : auth.isClient();
  const mergedEnquiriesWorkspaceId = projectsGranted && customerEnquiriesGranted ? customerEnquiriesWorkspaceId : null;
  useEffect(() => {
    // Preserve old customer Enquiries links, without broadening either raw grant.
    if (currentView !== 'workspace-pipeline' || !mergedEnquiriesWorkspaceId || workspaceNavigation.loading) return;
    window.history.replaceState({ ...window.history.state, view: 'projects' }, '', buildViewPath('projects') + window.location.search);
    setCurrentView('projects');
  }, [currentView, mergedEnquiriesWorkspaceId, workspaceNavigation.loading, setCurrentView]);
  const homeAllowed = !workspaceNavigation.authoritative || workspaceNavigation.navigation?.main.some(item => item.id === 'dashboard') === true;
  const previousViewRef = useRef(currentView);
  const canManageWorkspaceSettings = canManageActiveWorkspaceSettings(
    auth.activeWorkspaceId,
    auth.workspaceMemberships,
  ) || workspaceNavigation.navigation?.canManage === true;
  const requestedWorkspaceProfileTab = profileActiveTab.startsWith(WORKSPACE_PROFILE_TAB_PREFIX)
    ? profileActiveTab.slice(WORKSPACE_PROFILE_TAB_PREFIX.length)
    : null;
  const requestedWorkspaceBaseTab = requestedWorkspaceProfileTab?.split('/')[0] ?? null;
  const workspaceProfileTab = requestedWorkspaceBaseTab && isSelfServiceWorkspaceTab(requestedWorkspaceBaseTab)
    ? requestedWorkspaceBaseTab
    : 'overview';
  const workspaceUsersInviteOpen = requestedWorkspaceProfileTab === 'users/invite';
  const workspaceUsersSelectedUserSlug = requestedWorkspaceProfileTab
    ? getSelfServiceWorkspaceUserSlug(requestedWorkspaceProfileTab)
    : null;
  const workspaceBotsSelectedSlug = requestedWorkspaceProfileTab
    ? getSelfServiceWorkspaceBotSlug(requestedWorkspaceProfileTab)
    : null;
  const isWorkspaceProfileView = requestedWorkspaceProfileTab !== null;

  useEffect(() => {
    const openWorkspaceLibrary = (event: Event) => {
      const detail = (event as CustomEvent<OpenWorkspaceResourceLibraryDetail>).detail;
      if (!detail?.workspaceId) return;
      setResourceLibraryWorkspaceScope({ id: detail.workspaceId, name: detail.workspaceName });
      navigateToView('resource-library');
    };
    window.addEventListener(OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT, openWorkspaceLibrary);
    return () => window.removeEventListener(OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT, openWorkspaceLibrary);
  }, [navigateToView]);

  // A workspace-selected scope is only meaningful for the Resource Library
  // reached from that workspace. Leaving the view restores the main-nav entry
  // to the signed-in user's own workspace.
  useEffect(() => {
    if (previousViewRef.current === 'resource-library' && currentView !== 'resource-library') {
      setResourceLibraryWorkspaceScope(null);
    }
    previousViewRef.current = currentView;
  }, [currentView]);

  // Old AI Agents bookmarks now open the shared workspace directory.
  useEffect(() => {
    if (currentView !== 'system-settings'
      || !['agents', 'integrations/agents'].includes(systemSettingsActiveTab)) return;
    setProfileActiveTab('workspace/bots');
    navigateToView('profile');
  }, [currentView, systemSettingsActiveTab, setProfileActiveTab, navigateToView]);


  // A persisted or deep-linked `/add-project` URL opens the appropriate
  // project-list overlay rather than leaving the user on a separate page.
  useEffect(() => {
    if (currentView !== 'add-project') return;
    if (!auth.isAdmin()) {
      navigateToView('projects');
      return;
    }
    if (isDesktop) {
      setAddProjectActiveTab('contractor');
      setAddProjectCompletedTabs(new Set());
      setAddProjectInspectorOpen(true);
    } else if (NativeBridge.isIOSShell()) {
      NativeBridge.presentNativeAddProject();
    } else {
      setAddProjectSheetOpen(true);
    }
    navigateToView('projects');
  }, [isDesktop, currentView, navigateToView]);

  useEffect(() => {
    if (isDesktop || !addProjectInspectorOpen) return;
    setAddProjectInspectorOpen(false);
    if (NativeBridge.isIOSShell()) {
      NativeBridge.presentNativeAddProject();
    } else {
      setAddProjectSheetOpen(true);
    }
  }, [isDesktop, addProjectInspectorOpen]);

  useEffect(() => {
    if ((currentView !== 'projects' && currentView !== 'add-project') || notificationOpen || messagePanelOpen) {
      setAddProjectInspectorOpen(false);
    }
  }, [currentView, notificationOpen, messagePanelOpen]);

  const [isCompactMessageThreadOpen, setIsCompactMessageThreadOpen] = useState(false);
  const [messagesThreadInspectorOpen, setMessagesThreadInspectorOpen] = useState(false);
  const [takeoffTypesInspectorOpen, setTakeoffTypesInspectorOpen] = useState(false);
  useEffect(() => {
    const onTakeoffInspectorState = (event: Event) => {
      setTakeoffTypesInspectorOpen(
        !!(event as CustomEvent<{ open?: boolean }>).detail?.open,
      );
    };
    window.addEventListener('takeoff:inspector-state-change', onTakeoffInspectorState);
    return () => window.removeEventListener('takeoff:inspector-state-change', onTakeoffInspectorState);
  }, []);
  const setTakeoffInspectorHost = useCallback((host: HTMLDivElement | null) => {
    window.dispatchEvent(new CustomEvent('takeoff:inspector-host-change', { detail: { host } }));
  }, []);
  const showTakeoffTypesInspector = isDesktop
    && currentView === 'project-detail'
    && projectActiveTab === 'takeoff'
    && takeoffTypesInspectorOpen;
  const [supportInspectorOpen, setSupportInspectorOpen] = useState(false);
  const pricingOverviewAvailable = isPricingOverviewRoute && !si.open && !supportInspectorOpen && !notificationOpen && !messagePanelOpen;
  const openTasksForCreate = useCallback(() => {
    if (!taskQueueOpen) toggleTaskQueuePanel();
    else {
      if (notificationOpen) toggleNotificationsPanel();
      if (messagePanelOpen) toggleMessagesPanel();
    }
    setSupportInspectorOpen(false);
    si.close();
  }, [taskQueueOpen, toggleTaskQueuePanel, notificationOpen, toggleNotificationsPanel, messagePanelOpen, toggleMessagesPanel, si.close]);
  useCalendarTaskCreateIntent(currentView,
    taskQueueOpen && !notificationOpen && !messagePanelOpen && !supportInspectorOpen && !si.open,
    openTasksForCreate);
  useEffect(() => { if (notificationOpen || messagePanelOpen || supportInspectorOpen) si.close(); }, [notificationOpen, messagePanelOpen, supportInspectorOpen, si.close]);
  const aiFirstName = auth.user?.firstName || auth.user?.name?.trim().split(/\s+/)[0] || '';
  const quantisAIContent = <SIInspector
    firstName={aiFirstName}
    workspaceId={auth.activeWorkspaceId}
    view={currentView}
    projectId={currentView === 'project-detail' ? selectedProjectId : null}
    takeoffActive={currentView === 'project-detail' && projectActiveTab === 'takeoff'}
    presentation={currentView === 'quantis-ai' ? 'page' : 'panel'}
  />;
  const previousAIView = useRef<AppView>('dashboard');
  useEffect(() => {
    if (currentView === 'quantis-ai') si.close();
    else previousAIView.current = currentView;
  }, [currentView, si.close]);
  const toggleSIInspector = useCallback(() => {
    // Match native: leave the canvas before opening its contextual panel.
    if (currentView === 'quantis-ai') {
      navigateToView(previousAIView.current);
      closeRightPanels();
      setSupportInspectorOpen(false);
      si.show();
      return;
    }
    if (!si.open) { closeRightPanels(); setSupportInspectorOpen(false); }
    si.toggle();
  }, [currentView, navigateToView, si.open, si.toggle, si.show, closeRightPanels]);
  useEffect(() => {
    window.addEventListener(QUANTISAI_TOGGLE_EVENT, toggleSIInspector);
    return () => window.removeEventListener(QUANTISAI_TOGGLE_EVENT, toggleSIInspector);
  }, [toggleSIInspector]);
  // A question asked from search opens QuantisAI the same way its header button does.
  useEffect(() => {
    if (si.askPrompt) { closeRightPanels(); setSupportInspectorOpen(false); }
  }, [si.askPrompt, closeRightPanels]);
  const toggleSupportInspector = useCallback(() => {
    if (!supportInspectorOpen) closeRightPanels();
    setSupportInspectorOpen(open => !open);
  }, [closeRightPanels, supportInspectorOpen]);

  useEffect(() => {
    if (notificationOpen || messagePanelOpen) setSupportInspectorOpen(false);
  }, [notificationOpen, messagePanelOpen]);

  // The page and its inspector would show the same thing twice.
  useEffect(() => {
    if (currentView === 'support' || currentView === 'notifications') setSupportInspectorOpen(false);
  }, [currentView]);

  const [selectedContact, setSelectedContact] = useState<ContactPanelEntry | null>(null);
  const handleSelectContact = useCallback((entry: ContactPanelEntry | null) => {
    if (currentView === 'contacts') {
      const targetPath = entry
        ? buildPrettyContactPath({
            contactKey: entry.kind === 'user' ? entry.data.id : `mc-${entry.data.id}`,
            contactName: entry.label,
          })
        : '/contacts';
      if (window.location.pathname !== targetPath) {
        const state = entry
          ? { view: 'contacts', contactKey: entry.kind === 'user' ? entry.data.id : `mc-${entry.data.id}` }
          : { view: 'contacts' };
        if (entry) window.history.pushState(state, '', targetPath);
        else window.history.replaceState(state, '', targetPath);
      }
    }
    setSelectedContact(entry);
  }, [currentView]);
  // Clear a contact selection when the view changes so it can't leak across
  // views (Contacts renders it inline; Employers opens it in the shell panel).
  // Selecting within a view doesn't change currentView, so this only fires on nav.
  useEffect(() => {
    setSelectedContact(null);
  }, [currentView]);
  const handleAddProject = useCallback(() => {
    if (isDesktop) {
      if (addProjectInspectorOpen) {
        setAddProjectInspectorOpen(false);
        return;
      }
      closeRightPanels();
      setSelectedContact(null);
      setAddProjectActiveTab('contractor');
      setAddProjectCompletedTabs(new Set());
      setAddProjectInspectorOpen(true);
    } else if (NativeBridge.isIOSShell()) {
      // Native iOS: present a real system .sheet hosting the form.
      NativeBridge.presentNativeAddProject();
    } else {
      // Web PWA on mobile: the vaul drawer is the best we can do.
      setAddProjectSheetOpen(true);
    }
  }, [isDesktop, addProjectInspectorOpen, closeRightPanels, setAddProjectActiveTab, setAddProjectCompletedTabs]);

  const nativeBridge = useNativeBridge();
  const contactsHubDestinations = useMemo(() => getSidebarNavGroups({
    role: auth.user?.role ?? 'member',
    resolvedItems: workspaceNavigation.authoritative ? workspaceNavigation.navigation?.main ?? null : undefined,
  }).contactsHubDestinations, [auth.user?.role, workspaceNavigation.authoritative, workspaceNavigation.navigation]);

  // Warm up the Google Maps API key cache so it's in localStorage before the
  // user opens a project — the static map image URL is built synchronously from
  // the cached key with no async wait.
  useEffect(() => {
    const timer = setTimeout(() => { getGoogleMapsApiKey().catch(() => {}); }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Load the project_stages reference table once so stage labels/colours reflect any
  // DB edits. Fire-and-forget: stageUtils falls back to STAGE_INFO until this resolves.
  useEffect(() => {
    const timer = setTimeout(() => { ProjectStagesService.load().catch(() => {}); }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Bridge: header's + pill on the Workspaces list opens Add Workspace
  // as its own page (proper back-chevron navigation), not as a sub-tab.
  useEffect(() => {
    const onCreateWorkspace = () => navigateToView('add-workspace');
    window.addEventListener('workspaces:create-new', onCreateWorkspace);
    return () => window.removeEventListener('workspaces:create-new', onCreateWorkspace);
  }, [navigateToView]);
  const shouldHideWebHeaderForNativeShell =
    nativeBridge.isNative &&
    (
      nativeBridge.hasNativeTabBar ||
      nativeBridge.hasNativeSidebar ||
      nativeShellHidesWebMainHeader
    );
  const shouldRenderWorkspaceHeader =
    shouldPassWebHeader && !shouldHideWebHeaderForNativeShell;
  // Supervisor-shell routes — uses the canonical helper so legacy
  // 'supervisor' users still match. Post-migration there are no live
  // supervisor-role users (they're now `member` + workspace_user),
  // but the routes stay live so the in-flight session of any cached
  // legacy user keeps working.
  const isSupervisorShellRoute =
    auth.isSupervisor() &&
    (
      currentView === 'dashboard' ||
      currentView === 'progress' ||
      currentView === 'daily-diary' ||
      currentView === 'deliveries'
    );

  const handleUpdateUser = (updatedUser: any) => {
    if (!updatedUser?.id) return;

    auth.updateUser?.(updatedUser);
  };

  const projectTaskInspectorAllowed = (currentView === 'project-detail' && (projectActiveTab === 'tasks' || projectActiveTab === 'overview'))
    || (currentView === 'calendar' && calendarProjectScope !== null);

  const handleCloseShellRightPanel = () => {
    if (projectTaskInspectorAllowed
      && !supportInspectorOpen && !notificationOpen && !messagePanelOpen
      && document.querySelector('[data-project-task-inspector]')) {
      window.dispatchEvent(new Event('project-tasks:detail-back'));
      return;
    }
    setSupportInspectorOpen(false);
    if (currentView === 'project-detail' && projectActiveTab === 'site_photos') {
      window.dispatchEvent(new Event('site-photos:close-inspector'));
    }
    if (currentView === 'project-detail' && projectActiveTab === 'team') {
      window.dispatchEvent(new Event('project-members:close-detail'));
    }
    if (showTakeoffTypesInspector) {
      window.dispatchEvent(new Event('takeoff:inspector-close'));
      setTakeoffTypesInspectorOpen(false);
    }
    if (currentView === 'messages' && messagesThreadInspectorOpen) {
      window.dispatchEvent(new Event('messages:close-thread-inspector'));
      setMessagesThreadInspectorOpen(false);
    }
    closeRightPanels();
    setSelectedContact(null);
    setAddProjectInspectorOpen(false);
  };

  // A record's own inspector takes the split's column for a moment; QuantisAI comes back when it closes.
  const [siSplitParked, setSISplitParked] = useState(false);
  const showShellInspector = useCallback(() => {
    if (siSplit) setSISplitParked(true);
    closeRightPanels();
    setSupportInspectorOpen(false);
    si.close();
  }, [closeRightPanels, si.close, siSplit]);
  useEffect(() => { if (!siSplitTab || si.open) setSISplitParked(false); }, [siSplitTab, si.open]);
  const restoreSISplit = useCallback(() => { setSISplitParked(false); si.show(); }, [si.show]);
  const parkSISplit = useCallback(() => { setSISplitParked(true); si.close(); }, [si.close]);

  const handleNotificationNavigate = async (url: string, metadata?: Record<string, any>) => {
    const calendarTarget = normalizeCalendarEventTarget(metadata?.eventId, metadata?.occurrenceStart)
      ?? calendarEventTargetFromUrl(url);
    if (calendarTarget || metadata?.relatedEntityType === 'calendar_event') {
      if (!calendarTarget) throw new Error('Invalid calendar event destination');
      const displayed = queueCalendarEventTarget(calendarTarget);
      closeRightPanels();
      window.dispatchEvent(new CustomEvent('calendar:open-project', { detail: { projectId: null } }));
      await displayed;
      return;
    }
    if (metadata?.projectTab === 'pricing' && metadata?.commentId) {
      const projectId = metadata.projectId;
      const documentId = metadata.documentId;
      const itemId = metadata.itemId;
      const commentId = metadata.commentId;
      if (![projectId, documentId, itemId, commentId].every(value => typeof value === 'string' && value.length > 0)) {
        throw new Error('Incomplete BOQ comment destination');
      }
      const comment = await getBoqComment({ documentId, itemId, commentId });
      if (!comment) throw new Error('BOQ comment unavailable');
      const target = { projectId, documentId, itemId, commentId };
      queueBOQCommentTarget(target);
      const displayed = waitForBOQCommentTarget(target);
      closeRightPanels();
      handleSelectProjectTab(projectId, metadata.projectName || 'Project', 'pricing');
      await displayed;
      return;
    }
    if (metadata?.source === 'gmail') {
      if (metadata.messageId || metadata.threadId) {
        queueEmailNotificationOpen({
          notificationId: metadata.notificationId,
          messageId: metadata.messageId,
          threadId: metadata.threadId,
        });
      }

      closeRightPanels();
      setEmailActiveTab('INBOX');
      navigateToView('email');
      // Older alerts may not carry a durable Gmail id. They still have a
      // useful destination: the inbox. Only queue the one-shot exact-message
      // request when there is enough data for EmailView to select a message.
      return;
    }

    if (metadata?.chatId) {
      toggleMessagesPanel(
        String(metadata.chatId),
        typeof metadata.messageId === 'string' ? metadata.messageId : null
      );
      return;
    }

    if (metadata?.taskId) {
      const displayed = waitForTaskNotificationDisplay(auth.user?.id ?? '', String(metadata.taskId));
      closeRightPanels();
      handleShowCalendarWithTask(String(metadata.taskId));
      await displayed;
      return;
    }

    if (metadata?.projectId) {
      closeRightPanels();
      handleSelectProject(String(metadata.projectId), metadata.projectName || 'Project');
      return;
    }

    if (metadata?.clientId) {
      closeRightPanels();
      handleSelectClient(String(metadata.clientId), metadata.clientName || 'Client');
      return;
    }

    // Notification action_urls are stored path-style ('/email', '/messages',
    // '/dashboard'); AppViews are bare names ('email'). Strip the leading slash
    // before matching — otherwise path-only notifications (e.g. gmail email
    // alerts with action_url '/email') fell through here and did nothing (the
    // "clicking a notification is stale" bug).
    const viewName = url ? url.replace(/^\/+/, '') : url;
    if (viewName && isAppView(viewName)) {
      closeRightPanels();
      navigateToView(viewName);
    }
  };

  const handleSidebarNavigate = useCallback((view: string) => {
    if (view.startsWith(HS_DOCUMENT_SECTION_PREFIX)) {
      window.dispatchEvent(
        new CustomEvent('hs:navigate-document-section', {
          detail: { sectionId: view.slice(HS_DOCUMENT_SECTION_PREFIX.length) },
        }),
      );
      return;
    }
    if (view.startsWith(HS_EDITOR_SECTION_PREFIX)) {
      window.dispatchEvent(
        new CustomEvent('hs:navigate-editor-section', {
          detail: { sectionId: view.slice(HS_EDITOR_SECTION_PREFIX.length) },
        }),
      );
      return;
    }
    // When an interim application is open the primary sidebar drills into its
    // sections (see AppSidebar). These are synthetic ids, not app views: route
    // them to InterimApplication via the same window events the native bridge
    // uses, rather than falling through to navigateToView (which would leave a
    // blank view).
    if (view === INTERIM_CLOSE_APPLICATION_SENTINEL) {
      window.dispatchEvent(new CustomEvent('interim:close-application'));
      return;
    }
    if (view.startsWith(INTERIM_SECTION_PREFIX)) {
      window.dispatchEvent(
        new CustomEvent('interim:navigate-section', {
          detail: { sectionId: view.slice(INTERIM_SECTION_PREFIX.length) },
        }),
      );
      return;
    }
    // Same shape for a drilled-in employer: the sidebar shows the profile's
    // sections, and both the close row and the section rows are synthetic ids.
    if (view === EMPLOYERS_CLOSE_DETAIL_SENTINEL) {
      window.dispatchEvent(new CustomEvent(EMPLOYERS_CLEAR_SELECTION_EVENT));
      return;
    }
    if (view.startsWith(EMPLOYER_DETAIL_TAB_PREFIX)) {
      window.dispatchEvent(
        new CustomEvent(EMPLOYER_DETAIL_SET_SECTION_EVENT, {
          detail: { sectionId: view.slice(EMPLOYER_DETAIL_TAB_PREFIX.length) },
        }),
      );
      return;
    }
    switch (view) {
      case 'dashboard': handleShowDashboard(); break;
      case 'projects': handleShowProjects(); break;
      case 'tasks': handleShowCalendarWithTask(); break;
      case 'notifications': handleShowNotifications(); break;
      case 'messages': handleShowMessages(); break;
      case 'system-settings': handleShowSystemSettings(); break;
      case 'user-management': handleShowUserManagement(); break;
      case 'employers': handleShowMainContractors(); break;
      case 'resource-library': {
        const workspaceId = auth.user.workspace_id ?? auth.user.workspaceId ?? null;
        if (!workspaceId) {
          setResourceLibraryWorkspaceScope(null);
          navigateToView('resource-library');
          break;
        }
        const workspace = (auth.availableWorkspaces ?? []).find(
          (item: { id: string; name: string }) => item.id === workspaceId,
        );
        window.dispatchEvent(new CustomEvent<OpenWorkspaceResourceLibraryDetail>(
          OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT,
          {
            detail: {
              workspaceId,
              workspaceName: workspace?.name ?? auth.activeWorkspace?.name ?? 'My Workspace',
            },
          },
        ));
        break;
      }
      // Workspace Settings is a self-service route scoped to the active
      // membership. It now lives in Profile; keep this synthetic id as a
      // backwards-compatible entry point for cached menus and native shells.
      case 'workspace-settings': {
        if (canManageWorkspaceSettings) {
          setProfileActiveTab(`${WORKSPACE_PROFILE_TAB_PREFIX}overview`);
          navigateToView('profile');
        } else {
          navigateToView('dashboard');
        }
        break;
      }
      default: navigateToView(view as AppView); break;
    }
  }, [
    handleShowDashboard,
    handleShowProjects,
    handleShowCalendarWithTask,
    handleShowNotifications,
    handleShowMessages,
    handleShowSystemSettings,
    handleShowUserManagement,
    handleShowMainContractors,
    navigateToView,
    canManageWorkspaceSettings,
    setProfileActiveTab,
    auth.activeWorkspace,
    auth.user,
  ]);

  const ownResourceLibraryWorkspaceId = auth.user.workspace_id ?? auth.user.workspaceId ?? null;
  const ownResourceLibraryWorkspace = (auth.availableWorkspaces as Workspace[] | undefined)
    ?.find((workspace) => workspace.id === ownResourceLibraryWorkspaceId);
  const selectedResourceLibraryScope = resourceLibraryWorkspaceScope
    ?? (ownResourceLibraryWorkspaceId
      ? {
          id: ownResourceLibraryWorkspaceId,
          name: ownResourceLibraryWorkspace?.name ?? auth.activeWorkspace?.name ?? 'My Workspace',
        }
      : null);

  return (
    <CallProvider currentUser={auth.user}>
    <HuddleProvider currentUser={auth.user}>
      {/* Headless: presence broadcast for the macOS shell, where AppSidebar
          (the usual owner of this wiring) never mounts. */}
      <NativePresenceBridge user={auth.user} />
      <IncomingCallOverlay />
      <ActiveCallBar />
      <VoicemailOverlay />
      <MicErrorBanner />
      <ShortcutsSheet canAddProject={auth.isAdmin()} />
    <AppRightPanelProvider
      notificationOpen={!!notificationOpen}
      messagePanelOpen={!!messagePanelOpen}
      supportOpen={supportInspectorOpen || si.open}
      onShowShellInspector={showShellInspector}
    >
      {({ boqPanelOpen, boqBottomPanelOpen, sitePhotoPanelOpen, projectMemberPanelOpen, projectTaskPanelOpen, leftShellInspectorOpen, closeLeftShellInspector, shellInspectorOpen, closeShellInspector }) => (
    <>
    <SISplitRestorer split={siSplit} parked={siSplitParked} editorOpen={boqPanelOpen} inspectorOpen={shellInspectorOpen || boqPanelOpen} onPark={parkSISplit} onRestore={restoreSISplit} />
    <AppLayout
      isLeftPanelOpen={leftShellInspectorOpen}
      onCloseLeftPanel={closeLeftShellInspector}
      currentView={currentView}
      layoutPreset={shellConfig.preset}
      scrollMode={shellConfig.scrollMode}
      noPadding={shellConfig.noPadding}
      onNavigate={navigateToView as any}
      user={auth.user}
      onLogout={handleLogout}
      onShowNotifications={toggleNotificationsPanel}
      onShowMessages={toggleMessagesPanel}
      hideBottomNav={currentView === 'messages' && isCompactMessageThreadOpen}
      notificationOpen={notificationOpen}
      messagePanelOpen={messagePanelOpen}
      panelChatId={panelChatId}
      onClosePanels={closeRightPanels}
      isRightPanelOpen={
        si.open || shellInspectorOpen ||
        supportInspectorOpen ||
        notificationOpen ||
        messagePanelOpen ||
        (taskQueueOpen && currentView === 'calendar') ||
        (bookingPanelOpen && currentView === 'calendar') ||
        (messagesThreadInspectorOpen && currentView === 'messages') ||
        showTakeoffTypesInspector ||
        (addProjectInspectorOpen && currentView === 'projects' && auth.isAdmin()) ||
        (sitePhotoPanelOpen && currentView === 'project-detail' && projectActiveTab === 'site_photos') ||
        (projectMemberPanelOpen && currentView === 'project-detail' && projectActiveTab === 'team') ||
        (projectTaskPanelOpen && projectTaskInspectorAllowed) ||
        boqPanelOpen ||
        // Contacts drives the shell right panel again on desktop. Its old
        // two-pane split has gone: the list now spans the full width, and a
        // selected contact opens in the same panel every other view already
        // sends one to. Below lg, CompaniesView still shows its own full-screen
        // overlay, so the panel must stay closed there or both would render.
        (!!selectedContact && (currentView !== 'contacts' || isDesktop))
      }
      rightPanelWidth={
        siSplit
          ? SI_SPLIT_DEFAULT_WIDTH
          : (si.open || supportInspectorOpen || notificationOpen || messagePanelOpen || (taskQueueOpen && currentView === 'calendar'))
          ? `${isCompactDesktop ? 400 : isLaptopDesktop ? 440 : 480}px`
          : shellInspectorOpen
          ? `${isPricingOverviewRoute ? pricingOverviewWidth : isCompactDesktop ? 440 : isLaptopDesktop ? 480 : 560}px`
          : addProjectInspectorOpen && currentView === 'projects'
          ? `${isCompactDesktop ? 440 : isLaptopDesktop ? 480 : 560}px`
          : messagesThreadInspectorOpen && currentView === 'messages' && !notificationOpen && !messagePanelOpen
          ? `${isCompactDesktop ? 400 : isLaptopDesktop ? 440 : 480}px`
          : showTakeoffTypesInspector && !notificationOpen && !messagePanelOpen
          ? '320px'
          : sitePhotoPanelOpen && currentView === 'project-detail' && projectActiveTab === 'site_photos' && !notificationOpen && !messagePanelOpen
          ? `${isCompactDesktop ? 400 : isLaptopDesktop ? 440 : 480}px`
          : projectMemberPanelOpen && currentView === 'project-detail' && projectActiveTab === 'team' && !notificationOpen && !messagePanelOpen
          ? `${isCompactDesktop ? 400 : isLaptopDesktop ? 440 : 480}px`
          : projectTaskPanelOpen && projectTaskInspectorAllowed && !notificationOpen && !messagePanelOpen
          ? `${isCompactDesktop ? 440 : isLaptopDesktop ? 480 : 560}px`
          : bookingPanelOpen && currentView === 'calendar' && !notificationOpen && !messagePanelOpen
          ? `${isCompactDesktop ? 400 : isLaptopDesktop ? 440 : 480}px`
          : boqPanelOpen && currentView === 'resource-library' && !notificationOpen && !messagePanelOpen
          ? `${isCompactDesktop ? 440 : isLaptopDesktop ? 480 : 560}px`
          : boqPanelOpen && !notificationOpen && !messagePanelOpen
          ? `${boqInspectorDefaultWidth}px`
          : undefined
      }
      rightPanelDetached={
        (si.open && !siSplit) || shellInspectorOpen || supportInspectorOpen || notificationOpen || messagePanelOpen || (
          (projectTaskPanelOpen && projectTaskInspectorAllowed) ||
          (addProjectInspectorOpen && currentView === 'projects') ||
          showTakeoffTypesInspector ||
          (sitePhotoPanelOpen && currentView === 'project-detail' && projectActiveTab === 'site_photos') ||
          (projectMemberPanelOpen && currentView === 'project-detail' && projectActiveTab === 'team') ||
          (messagesThreadInspectorOpen && currentView === 'messages') ||
          (taskQueueOpen && currentView === 'calendar') ||
          (bookingPanelOpen && currentView === 'calendar') ||
          boqPanelOpen
        )
      }
      rightPanelSplit={siSplit}
      rightPanelResizable
      rightPanelMinWidth={shellInspectorOpen && pricingOverviewAvailable ? 240 : si.open ? 320 : showTakeoffTypesInspector ? 300 : 400}
      rightPanelMaxWidth={siSplit ? undefined : si.open ? 800 : showTakeoffTypesInspector ? 480 : boqInspectorMaxWidth}
      rightPanelSiblingMinWidth={siSplit ? SI_SPLIT_RECORD_MIN_WIDTH : 360}
      rightPanelWidthStorageKey={siSplit ? 'quantis:si-split-width' : si.open ? 'quantis:si-inspector-width' : supportInspectorOpen ? 'quantis:support-inspector-width' : notificationOpen ? 'quantis:notifications-inspector-width' : messagePanelOpen ? 'quantis:messages-inspector-width' : shellInspectorOpen ? (isPricingOverviewRoute ? 'quantis:pricing-overview-inspector-width' : 'quantis:shell-inspector-width') : taskQueueOpen && currentView === 'calendar' ? 'quantis:calendar-tasks-inspector-width' : showTakeoffTypesInspector ? 'quantis:takeoff-types-inspector-width' : sitePhotoPanelOpen ? 'quantis:site-photo-inspector-width' : projectMemberPanelOpen ? 'quantis:project-member-inspector-width' : 'quantis:boq-inspector-width'}
      panelMotionDurationMs={boqPanelOpen ? BOQ_PANEL_MOTION_DURATION_MS : undefined}
      rightPanelMotionKind={currentView === 'project-detail' && (projectActiveTab === 'site_photos' || projectActiveTab === 'team') ? 'slide' : 'panel'}
      onCloseRightPanel={si.open ? si.close : shellInspectorOpen && !supportInspectorOpen && !notificationOpen && !messagePanelOpen
        ? closeShellInspector : handleCloseShellRightPanel}
      isBottomPanelOpen={boqBottomPanelOpen}
      bottomPanelDetached={boqBottomPanelOpen}
      bottomPanelResizable
      bottomPanelMinHeight={104}
      bottomPanelSiblingMinHeight={240}
      bottomPanelHeightStorageKey="quantis:boq-data-sheet-height"
      bottomPanelAutoFitRows
      projectContext={{
        isProjectDetailView: currentView === 'project-detail',
        projectActiveTab,
        onProjectTabChange: setProjectActiveTab,
        projectTabProps: projectTabProps || undefined,
        projectPermissions,
      }}
      sidebar={
        <AppSidebar
          user={auth.user}
          currentView={currentView}
          onNavigate={handleSidebarNavigate}
          onShowProfile={() => navigateToView('account')}
          onLogout={handleLogout}
          onShowUserManagement={handleShowUserManagement}
          onShowWorkspaces={() => navigateToView('workspaces')}
          onShowAdminSupervisorManagement={() => navigateToView('supervisor-management')}
          isOrgManager={canManageWorkspaceSettings}
          isProjectDetailView={currentView === 'project-detail'}
          projectActiveTab={projectActiveTab}
          documentPreviewOpen={!!documentRouteSlug}
          onProjectTabChange={setProjectActiveTab}
          projectTabProps={projectTabProps || undefined}
          projectPermissions={projectPermissions}
          notificationOpen={notificationOpen}
          onToggleNotifications={toggleNotificationsPanel}
          supportOpen={supportInspectorOpen}
          onToggleSupport={toggleSupportInspector}
          isSystemSettingsView={currentView === 'system-settings'}
          systemSettingsActiveTab={systemSettingsActiveTab}
          onSystemSettingsTabChange={setSystemSettingsActiveTab}
          isPreferencesView={currentView === 'preferences'}
          preferencesActiveTab={preferencesSection}
          onPreferencesTabChange={setPreferencesSection}
          isProfileView={currentView === 'profile' || currentView === 'account'}
          profileActiveTab={profileActiveTab}
          onProfileTabChange={setProfileActiveTab}
          isEmailView={currentView === 'email'}
          emailActiveTab={emailActiveTab}
          onEmailTabChange={setEmailActiveTab}
          isWorkspacesView={currentView === 'workspaces' || currentView === 'add-workspace' || currentView === 'client-detail'}
          workspacesActiveTab={currentView === 'add-workspace' ? 'add-client' : workspacesActiveTab}
          onWorkspacesTabChange={(tab) => {
            // Add Workspace is its own page now — divert to /workspaces/add-workspace
            // instead of switching the in-page sub-tab.
            if (tab === 'add-client') {
              navigateToView('add-workspace');
              return;
            }
            setWorkspacesActiveTab(tab);
            if (currentView !== 'workspaces') {
              navigateToView('workspaces');
            }
          }}
          workspacesStats={workspacesStats}
        />
      }
      rightPanel={
        si.open && currentView !== 'quantis-ai' ? quantisAIContent : supportInspectorOpen ? (
          <Suspense fallback={null}>
            <SupportModal embedded open onClose={() => setSupportInspectorOpen(false)} />
          </Suspense>
        ) : notificationOpen ? (
          <Suspense fallback={null}>
            <NotificationsSidePanel
              isOpen={notificationOpen}
              onClose={closeRightPanels}
              onNavigate={handleNotificationNavigate}
            />
          </Suspense>
        ) : messagePanelOpen ? (
          <Suspense fallback={null}>
            <MessagesSidePanel
              onClose={closeRightPanels}
              initialChatId={panelChatId}
              initialThreadMessageId={panelThreadMessageId}
              projectId={currentView === 'project-detail' ? selectedProjectId : null}
              projectName={currentView === 'project-detail' ? selectedProjectName : null}
              projectParticipantIds={
                currentView === 'project-detail' && projectTabProps?.internalUserIds
                  ? projectTabProps.internalUserIds
                  : undefined
              }
            />
          </Suspense>
        ) : shellInspectorOpen ? (
          <></>
        ) : taskQueueOpen && currentView === 'calendar' ? (
          <Suspense fallback={null}>
            <CalendarTasksSidePanel
              user={{
                id: auth.user.id,
                name: auth.user.name ?? auth.user.full_name ?? '',
                role: auth.user.role,
                company_id: auth.user.company_id,
              }}
              initialTaskId={initialTaskId}
            />
          </Suspense>
        ) : bookingPanelOpen && currentView === 'calendar' ? (
          <Suspense fallback={null}>
            <BookingSidePanel
              isOpen={bookingPanelOpen}
              onClose={closeRightPanels}
              hostName={auth.user?.name ?? auth.user?.full_name ?? null}
              workingSchedule={auth.user?.working_schedule ?? auth.user?.preferences?.workingSchedule ?? null}
              onShowProfile={() => {
                setProfileActiveTab('details');
                navigateToView('account');
              }}
            />
          </Suspense>
        ) : messagesThreadInspectorOpen && currentView === 'messages' ? (
          <div id="messages-thread-inspector-host" className="h-full min-h-0 w-full" />
        ) : showTakeoffTypesInspector ? (
          <div ref={setTakeoffInspectorHost} id="takeoff-types-inspector-host" className="h-full min-h-0 w-full" />
        ) : sitePhotoPanelOpen && currentView === 'project-detail' && projectActiveTab === 'site_photos' ? (
          undefined
        ) : projectMemberPanelOpen && currentView === 'project-detail' && projectActiveTab === 'team' ? (
          undefined
        ) : projectTaskPanelOpen && projectTaskInspectorAllowed ? (
          undefined
        ) : addProjectInspectorOpen && currentView === 'projects' && auth.isAdmin() ? (
          <Suspense fallback={null}>
            <AddProjectInspector
              currentUser={auth.user}
              onClose={() => setAddProjectInspectorOpen(false)}
              onProjectCreated={(projectId) => {
                setAddProjectInspectorOpen(false);
                const project = DataService.getProject(projectId);
                handleSelectProjectTab(projectId, project?.name || 'Project', 'documents');
              }}
              onAddMainContractor={() => {
                setAddProjectInspectorOpen(false);
                handleShowMainContractors('add-contractor');
              }}
              activeTab={addProjectActiveTab}
              onTabChange={setAddProjectActiveTab}
              completedTabs={addProjectCompletedTabs}
              onCompletedTabsChange={setAddProjectCompletedTabs}
            />
          </Suspense>
        ) : (selectedContact && (currentView !== 'contacts' || isDesktop)) ? (
          <Suspense fallback={null}>
            <ContactProfilePanel
              entry={selectedContact}
              onClose={() => handleSelectContact(null)}
              currentUserId={auth.user.id}
            />
          </Suspense>
        ) : undefined
      }
      header={shouldRenderWorkspaceHeader ? (
        <UnifiedAppHeader
          user={auth.user}
          currentView={currentView}
          pageTitle={pageTitle}
          onNavigate={navigateToView as any}
          onToggleNotifications={toggleNotificationsPanel}
          onToggleMessages={toggleMessagesPanel}
          onToggleSupport={toggleSupportInspector}
          onToggleSI={toggleSIInspector}
          onToggleTaskQueue={toggleTaskQueuePanel}
          onToggleBooking={toggleBookingPanel}
          notificationOpen={notificationOpen}
          messagePanelOpen={messagePanelOpen}
          supportInspectorOpen={supportInspectorOpen}
          taskQueueOpen={taskQueueOpen}
          bookingPanelOpen={bookingPanelOpen}
          onShowProfile={() => navigateToView('account')}
          onLogout={handleLogout}
          // Project detail tabs
          isProjectDetailView={currentView === 'project-detail'}
          projectActiveTab={projectActiveTab}
          documentPreviewOpen={!!documentRouteSlug}
          onProjectTabChange={setProjectActiveTab}
          projectTabProps={projectTabProps || undefined}
          projectPermissions={projectPermissions}
          // System settings tabs
          isSystemSettingsView={currentView === 'system-settings'}
          systemSettingsActiveTab={systemSettingsActiveTab}
          onSystemSettingsTabChange={setSystemSettingsActiveTab}
          isPreferencesView={currentView === 'preferences'}
          preferencesActiveTab={preferencesSection}
          onPreferencesTabChange={setPreferencesSection}
          isProfileView={currentView === 'profile' || currentView === 'account'}
          profileActiveTab={profileActiveTab}
          onProfileTabChange={setProfileActiveTab}
          // Email folder tabs
          isEmailView={currentView === 'email'}
          emailActiveTab={emailActiveTab}
          onEmailTabChange={setEmailActiveTab}
          // Client management tabs
          isWorkspacesView={currentView === 'workspaces' || currentView === 'add-workspace' || currentView === 'client-detail'}
          workspacesActiveTab={currentView === 'add-workspace' ? 'add-client' : workspacesActiveTab}
          onWorkspacesTabChange={(tab: string) => {
            // Add Workspace is its own page now — divert to /workspaces/add-workspace
            // instead of switching the in-page sub-tab.
            if (tab === 'add-client') {
              navigateToView('add-workspace');
              return;
            }
            setWorkspacesActiveTab(tab);
            if (currentView !== 'workspaces') {
              navigateToView('workspaces');
            }
          }}
          workspacesStats={workspacesStats}
          // Project list filters
          isProjectListView={currentView === 'projects'}
          projectListProps={projectListProps || undefined}
        />
      ) : undefined}
    >
      <Suspense fallback={null}>
        <>
        {/* Preserve Home across navigation; hidden Activity stops effects and
            restores them on return. Reset all state for another user/workspace. */}
        {auth.isClient() && homeAllowed && (
          <Activity key={`${auth.user?.id}:${auth.activeWorkspaceId}`} mode={currentView === 'dashboard' ? 'visible' : 'hidden'}>
            <ClientDashboard
              user={auth.user}
              company={auth.company || (auth.user.companyId || auth.user.company_id ? getCompany(auth.user.companyId || auth.user.company_id) : undefined)}
              currentView="dashboard"
              onProjectSelect={handleSelectProject}
            />
          </Activity>
        )}
        {!navigationAllowed ? (
          <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4 p-6 text-sm text-muted-foreground" role="status">
            {workspaceNavigation.loading ? 'Checking workspace access...' : workspaceNavigation.error ? 'Unable to check workspace access. Please try again.' : (
              <>
                <span>You do not have access to this screen.</span>
                {auth.activeWorkspaceId && (
                  <RequestAccess
                    audience="workspace"
                    target={buildAccessRequestTarget(window.location.pathname, { workspaceId: auth.activeWorkspaceId, section: currentView })!}
                  />
                )}
              </>
            )}
          </div>
        ) : <>
        {currentView === 'quantis-ai' && (
          <section aria-label="QuantisAI" className="flex h-full min-h-0 w-full flex-col">
            {quantisAIContent}
          </section>
        )}
        {(currentView === 'dashboard' || isSupervisorShellRoute) && !auth.isClient() && (
          auth.isSupervisor() ? (
            <UserDashboard user={auth.user} currentView={currentView} onNavigate={setCurrentView as any} />
          ) : <QSDashboard user={auth.user} />
        )}

        {currentView === 'projects' && auth.user && (
          <ProjectListView
            user={auth.user}
            customerEnquiriesWorkspaceId={mergedEnquiriesWorkspaceId}
            onViewProject={handleSelectProject}
            initialStage={projectFilterStage as any}
            // Creating projects is internal-team only (super_admin / internal).
            // Undefined for everyone else hides every add-project entry point
            // (header +, empty-state button, ITT button, desktop toolbar).
            onAddProject={auth.isAdmin() ? handleAddProject : undefined}
          />
        )}

        {currentView === 'project-detail' && selectedProjectId && (
          <ProjectDetail
            key={selectedProjectId}
            projectId={selectedProjectId}
            onClose={handleCloseProjectDetail}
            activeTab={projectActiveTab}
            requestedDocumentSlug={documentRouteSlug}
            onDocumentViewerChange={(document: GoogleDriveFile | null) => {
              setDocumentRouteSlug(document ? slugifySegment(document.name) : null);
            }}
            requestedEventsRoute={eventsRouteSlug}
            onEventsRouteChange={setEventsRouteSlug}
            onTabChange={setProjectActiveTab}
            onPermissionsLoad={setProjectPermissions}
            onNotesCountChange={onNotesCountChange}
          />
        )}

        {currentView === 'calendar' && auth.user && (
          <Suspense fallback={null}>
            <ScopedCalendarPage
              key={calendarProjectScope ?? 'personal'}
              scope={calendarProjectScope}
              user={auth.user}
              focusTaskId={selectedTaskId}
              onNavigateToProject={handleSelectProject}
            />
          </Suspense>
        )}

        {currentView === 'notifications' && (
          <NotificationsView
            onNavigate={handleNotificationNavigate}
            onClose={() => navigateToView('dashboard')}
          />
        )}

        {currentView === 'support' && (
          <Suspense fallback={null}>
            <div className="h-full min-h-0 w-full">
              <SupportModal embedded open onClose={() => navigateToView('dashboard')} />
            </div>
          </Suspense>
        )}

        {currentView === 'interim-valuations' && (auth.isAdmin() || auth.isStaff()) && (
          <InterimValuationsView currentUser={auth.user} initialTab={interimInitialTab} />
        )}

        {currentView === 'sales' && canOpenFinanceHubs(auth) && (
          <SalesView currentUser={auth.user} />
        )}

        {currentView === 'cis' && canOpenFinanceHubs(auth) && (
          <CISReturnsView currentUser={auth.user} />
        )}

        {currentView === 'purchases' && canOpenFinanceHubs(auth) && (
          <PurchasesView currentUser={auth.user} />
        )}

        {currentView === 'payroll' && canOpenFinanceHubs(auth) && (
          <Suspense fallback={null}>
            <PayrollView currentUser={auth.user} />
          </Suspense>
        )}

        {currentView === 'accounting' && canOpenFinanceHubs(auth) && (
          <Suspense fallback={null}>
            <AccountingView />
          </Suspense>
        )}

        {currentView === 'tax' && canOpenFinanceHubs(auth) && (
          <Suspense fallback={null}>
            <TaxView currentUser={auth.user} />
          </Suspense>
        )}

        {currentView === 'system-settings' && auth.isAdmin() && (
          <SystemSettings
            user={auth.user}
            onClose={handleShowDashboard}
            activeSection={systemSettingsActiveTab}
            onSectionChange={setSystemSettingsActiveTab}
          />
        )}

        {currentView === 'preferences' && (
          <Suspense fallback={null}>
            <PreferencesView user={auth.user} activeSection={preferencesSection} onSectionChange={setPreferencesSection} />
          </Suspense>
        )}

        {auth.user && <SettingsModal user={auth.user} />}

        {currentView === 'admin' && auth.isAdmin() && (
          <AdaptiveDeviceTemplate eyebrow="System" title="Administration" description="Core operational areas for managing users, workspaces, billing and platform controls.">
            <TemplateSection title="Control Centre" description="Use these entry points when extending the internal management surface.">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {([
                  { icon: User, label: 'User Management', onClick: handleShowUserManagement },
                  { icon: Building2, label: 'Workspaces', onClick: () => navigateToView('workspaces') },
                  { icon: Building2, label: 'Employers', onClick: handleShowMainContractors },
                  { icon: Info, label: 'Settings', onClick: handleShowSystemSettings },
                  { icon: ShieldCheck, label: 'Sales & Billing', onClick: handleShowFinanceBilling },
                ] as const).map(({ icon: Icon, label, onClick }) => (
                  <button
                    key={label}
                    onClick={() => onClick()}
                    className="flex flex-col items-center justify-center gap-3 rounded-card border bg-card text-card-foreground p-8 hover:bg-accent transition-colors cursor-pointer font-apple text-center"
                  >
                    <Icon className="shrink-0 text-primary" style={{ width: 28, height: 28 }} />
                    <span style={{ fontSize: 'var(--text-menu)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)' }}>{label}</span>
                  </button>
                ))}
              </div>
            </TemplateSection>
          </AdaptiveDeviceTemplate>
        )}

        {currentView === 'finance-billing' && auth.isAdmin() && (
          <FinanceBilling user={auth.user} onClose={handleShowAdminPanel} />
        )}

        {currentView === 'email' && canOpenEmail(auth) && (
          <Suspense fallback={null}>
            {/* Group inboxes piggy-back on the email view's chrome — when
                the active sidebar item is `group:<id>` (injected by
                AppSidebar from the email_groups registry) we render the
                shared-inbox body in place of the personal Gmail one. */}
            {emailActiveTab?.startsWith('group:') ? (
              <SharedInboxView
                workspaceId={(auth.user as any).workspace_id ?? (auth.user as any).workspaceId ?? null}
                userId={auth.user.id}
                groupId={emailActiveTab.slice('group:'.length)}
              />
            ) : (
              <EmailView activeLabel={emailActiveTab} onMailboxChange={setEmailActiveTab} />
            )}
          </Suspense>
        )}

        {currentView === 'email' && !canOpenEmail(auth) && (
          <div className="flex h-full items-center justify-center p-6 text-center" role="status">
            <div>
              <h2 className="text-base font-semibold">Email isn’t available for this account</h2>
              <p className="mt-2 text-sm text-muted-foreground">Use your email provider to access your mailbox.</p>
            </div>
          </div>
        )}

        {currentView === 'contacts' && (
          <Suspense fallback={null}>
            <CompaniesView
              user={auth.user}
              isStaff={auth.isStaff()}
              isAdmin={auth.isAdmin()}
              destinations={contactsHubDestinations}
              onSelectContact={handleSelectContact}
              onOpenClient={handleSelectClient}
              selectedContactKey={
                selectedContact
                  ? selectedContact.kind === 'user'
                    ? selectedContact.data.id
                    : `mc-${selectedContact.data.id}`
                  : null
              }
              selectedEntry={selectedContact}
            />
          </Suspense>
        )}

        {currentView === 'pipeline' && auth.isAdmin() && (
          <Suspense fallback={null}>
            <ConsultancyPipelineView />
          </Suspense>
        )}

        {currentView === 'workspace-pipeline' && (
          <Suspense fallback={null}>
            <WorkspacePipelineView
              workspaceId={(auth.user as any).workspace_id ?? (auth.user as any).workspaceId ?? ''}
              companyId={(auth.user as any).companyId ?? (auth.user as any).company_id ?? undefined}
              onSelectProject={handleSelectProject}
            />
          </Suspense>
        )}

        {currentView === 'employers' && (auth.isAdmin() || auth.isStaff()) && (
          <EmployerManagement
            currentUser={auth.user}
            onClose={handleShowDashboard}
            initialTab={mainContractorInitialTab}
            onInitialTabChange={setMainContractorInitialTab}
            onOpenContact={(entry) => {
              setSelectedContact(entry);
            }}
            onExitToDirectory={() => { markReturnToCompanies(); navigateToView('contacts'); }}
          />
        )}

        {currentView === 'supervisor-management' && (auth.isAdmin() || auth.isStaff()) && (
          <AdminSupervisorManagement currentUser={auth.user} onClose={handleShowDashboard} />
        )}

        {currentView === 'user-management' && auth.canAccess('manage_users') && (
          <UserManagement
            currentUser={auth.user}
            onAddUser={handleShowAddUserForm}
            newUser={newlyCreatedUser}
            onUserAdded={() => {
              setNewlyCreatedUser(null);
              setSelectedUserId(null);
            }}
            initialUserId={selectedUserId || undefined}
            activeTab={userManagementActiveTab}
            onTabChange={setUserManagementActiveTab}
          />
        )}

        {currentView === 'user-form' && auth.canAccess('manage_users') && (
          <AddUserForm
            currentUser={auth.user}
            onBack={handleShowUserManagement}
            onUserCreated={(user) => {
              setNewlyCreatedUser(user);
              handleShowUserManagement();
            }}
          />
        )}

        {currentView === 'workspaces' && canOpenWorkspaceDirectory(auth) && (
          <WorkspaceList
            currentUser={auth.user}
            onSelectWorkspace={handleSelectClient}
            activeTab={workspacesActiveTab}
            onTabChange={setWorkspacesActiveTab}
            initialSelectedClientId={selectedClientId}
            stats={workspacesStats}
            onExitToDirectory={handleCloseClientDetail}
          />
        )}

        {currentView === 'add-workspace' && auth.isInternal() && (
          <WorkspaceList
            currentUser={auth.user}
            onSelectWorkspace={handleSelectClient}
            activeTab="add-client"
            onTabChange={(tab) => {
              if (tab !== 'add-client') {
                navigateToView('workspaces');
                setWorkspacesActiveTab(tab);
              }
            }}
            stats={workspacesStats}
          />
        )}

        {currentView === 'client-detail' && selectedClientId && auth.isInternal() && (
          <WorkspaceDetail
            clientId={selectedClientId}
            onClose={handleCloseClientDetail}
            currentUser={auth.user}
            hideInternalNav={!nativeBridge.isNative}
            activeTab={clientDetailActiveTab}
            onTabChange={setClientDetailActiveTab}
          />
        )}

        {currentView === 'resource-library' && (auth.isAdmin() || auth.isStaff() || auth.isClient()) && (
          <Suspense fallback={null}>
            <ResourceLibraryManagement
              key={selectedResourceLibraryScope?.id ?? 'resource-library-unscoped'}
              currentUserId={auth.user.id}
              currentUserRole={auth.user.role ?? 'staff'}
              currentWorkspaceId={
                resourceLibraryWorkspaceScope?.id
                ?? auth.user.workspace_id
                ?? auth.user.workspaceId
                ?? null
              }
              availableWorkspaces={auth.availableWorkspaces ?? []}
              workspaceScope={selectedResourceLibraryScope}
            />
          </Suspense>
        )}

        {currentView === 'plant' && (auth.isAdmin() || auth.isStaff()) && (
          <Suspense fallback={null}>
            <PlantRegisterView
              currentWorkspaceId={auth.user.workspace_id ?? auth.user.workspaceId ?? null}
            />
          </Suspense>
        )}

        {currentView === 'outreach' && canOpenOutreach(auth) && (
          <Suspense fallback={null}>
            <OutreachView
              initialSection={isOutreachSectionId(outreachActiveTab) ? outreachActiveTab : undefined}
              currentWorkspaceId={auth.user.workspace_id ?? auth.user.workspaceId ?? null}
            />
          </Suspense>
        )}

        {currentView === 'request-client' && (auth.isAdmin() || auth.isStaff()) && (
          <WorkspaceRequestForm
            currentUser={auth.user}
            onClose={handleShowDashboard}
            onSubmitted={() => {
              handleShowDashboard();
              NotificationService.success('Client request submitted successfully');
            }}
            activeTab={clientRequestActiveTab}
            onTabChange={setClientRequestActiveTab}
            completedTabs={clientRequestCompletedTabs}
            onCompletedTabsChange={setClientRequestCompletedTabs}
          />
        )}

        {currentView === 'profile' && isWorkspaceProfileView && (() => {
          const companyId = auth.activeWorkspace?.companyId ?? null;
          // Platform agent administration does not grant wider workspace settings access.
          if (workspaceProfileTab === 'bots' && auth.user.role === 'super_admin') {
            return auth.activeWorkspaceId ? (
              <WorkspaceBotsSection
                workspaceId={auth.activeWorkspaceId}
                selectedSlug={workspaceBotsSelectedSlug}
                onSelectedSlugChange={(slug) => setProfileActiveTab(`workspace/bots${slug ? `/${slug}` : ''}`)}
                currentUserId={auth.user.id}
                currentUserRole={auth.user.role}
              />
            ) : <p className="p-8 text-sm text-muted-foreground">Select a workspace to manage AI agents.</p>;
          }
          if (workspaceProfileTab === 'integrations') {
            return <Suspense fallback={null}><SystemSettings user={auth.user} workspaceMode activeSection={requestedWorkspaceProfileTab || 'integrations'} onClose={() => setProfileActiveTab('overview')} onSectionChange={section => setProfileActiveTab(`${WORKSPACE_PROFILE_TAB_PREFIX}${section}`)} /></Suspense>;
          }
          if (!canManageWorkspaceSettings || !companyId) {
            return (
              <div className="h-full flex items-center justify-center bg-background">
                <div className="text-center max-w-sm space-y-2 p-8">
                  <h2 className="font-apple text-lg font-semibold text-foreground">
                    {!canManageWorkspaceSettings ? 'Restricted' : 'No active workspace'}
                  </h2>
                  <p className="font-apple text-sm text-muted-foreground">
                    {!canManageWorkspaceSettings
                      ? 'Workspace settings are only visible to workspace owners and admins.'
                      : 'Switch to a workspace to manage its organisation profile.'}
                  </p>
                </div>
              </div>
            );
          }
          return (
            <WorkspaceDetail
              clientId={companyId}
              onClose={() => setProfileActiveTab('overview')}
              currentUser={auth.user}
              activeTab={workspaceProfileTab}
              onTabChange={(tab) => {
                if (isSelfServiceWorkspaceTab(tab)) {
                  setProfileActiveTab(`${WORKSPACE_PROFILE_TAB_PREFIX}${tab}`);
                }
              }}
              hideInternalNav
              membershipMode="self-service"
              usersInviteOpen={workspaceUsersInviteOpen}
              onUsersInviteOpenChange={(open) => {
                setProfileActiveTab(`${WORKSPACE_PROFILE_TAB_PREFIX}users${open ? '/invite' : ''}`);
              }}
              usersSelectedUserSlug={workspaceUsersSelectedUserSlug}
              onUsersSelectedUserSlugChange={(userSlug) => {
                setProfileActiveTab(
                  `${WORKSPACE_PROFILE_TAB_PREFIX}users${userSlug ? `/${userSlug}` : ''}`,
                );
              }}
              botsWorkspaceId={auth.activeWorkspaceId ?? null}
              botsSelectedSlug={workspaceBotsSelectedSlug}
              onBotsSelectedSlugChange={(slug) => {
                setProfileActiveTab(`${WORKSPACE_PROFILE_TAB_PREFIX}bots${slug ? `/${slug}` : ''}`);
              }}
            />
          );
        })()}

        {currentView === 'profile' && !isWorkspaceProfileView && (
          <UserProfile
            user={auth.user}
            onClose={handleShowDashboard}
            onUpdateUser={handleUpdateUser}
            onLogout={handleLogout}
            activeTab={profileActiveTab}
            onTabChange={setProfileActiveTab}
          />
        )}

        {currentView === 'account' && (
          <UserProfile
            user={auth.user}
            onClose={handleShowDashboard}
            onUpdateUser={handleUpdateUser}
            onLogout={handleLogout}
            activeTab={profileActiveTab}
            onTabChange={setProfileActiveTab}
          />
        )}

        {/* Mobile: same form, presented as a bottom sheet over the
            projects list. The Sheet Root stays mounted (so it animates in
            and out); the form only mounts while open, so its effects don't
            run when the sheet is closed. */}
        {!isDesktop && auth.isAdmin() && auth.user && (
          <Suspense fallback={null}>
            <AddProjectSheet
              open={addProjectSheetOpen}
              onOpenChange={setAddProjectSheetOpen}
              currentUser={auth.user}
              onClose={() => setAddProjectSheetOpen(false)}
              onProjectCreated={(projectId) => {
                const project = DataService.getProject(projectId);
                const projectName = project?.name || 'Project';
                handleSelectProjectTab(projectId, projectName, 'documents');
              }}
              onAddMainContractor={() => handleShowMainContractors('add-contractor')}
              activeTab={addProjectActiveTab}
              onTabChange={setAddProjectActiveTab}
              completedTabs={addProjectCompletedTabs}
              onCompletedTabsChange={setAddProjectCompletedTabs}
            />
          </Suspense>
        )}

        {currentView === 'billing' && <MembershipView />}

        {currentView === 'messages' && (
          <MessagesView
            initialChatId={pendingChatId}
            initialThreadMessageId={panelThreadMessageId}
            onInitialChatConsumed={() => setPendingChatId(null)}
            initialChatSlug={pendingChatSlug}
            onInitialChatSlugConsumed={() => setPendingChatSlug(null)}
            onBackToApp={() => navigateToView('dashboard')}
            onCompactThreadStateChange={setIsCompactMessageThreadOpen}
            threadInspectorInShell={isDesktop}
            onThreadInspectorOpenChange={setMessagesThreadInspectorOpen}
          />
        )}

        {currentView === 'search' && (
          <MobileSearchPage
            onNavigateToProject={handleSelectProject}
          />
        )}

        {currentView === 'more' && (
          <MoreView
            user={auth.user}
            onNavigate={handleSidebarNavigate}
          />
        )}
        </>}
        </>
      </Suspense>
    </AppLayout>
    </>
      )}
    </AppRightPanelProvider>
    </HuddleProvider>
    </CallProvider>
  );
}

AuthenticatedWorkspace.displayName = 'AuthenticatedWorkspace';
